import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { SalesInsightEventPayload, SalesInsightEvents } from '../../analytics/types/analytics-events.ts';

type SingleInsightEvents = Exclude<
    keyof SalesInsightEvents,
    'sales_insight_list_rendered' | 'sales_insight_sort_dropdown_clicked' | 'sales_insight_section_expanded'
>;

export type SendAnalyticsEventData<T extends SingleInsightEvents = SingleInsightEvents> =
    T extends keyof SalesInsightEvents // the conditional distributes the type param into a union
        ? {
              event: T;
              payload: Omit<
                  SalesInsightEvents[T],
                  Exclude<keyof SalesInsightEventPayload, 'sales_insight_id'>
              >;
          }
        : never;

export type SendAnalyticsEventHandler = (event: SendAnalyticsEventData) => void;

export type UseInsightsAnalyticsOptions = {
    customerId: string;
};

export type InsightSortBy = 'recommended' | 'value';

type InsightsAnalyticsHook = {
    insights: (InsightListItem & { key: string })[];
    isLoading: boolean;
    sortBy: InsightSortBy;
    setSortBy: (sortBy: InsightSortBy) => void;

    sendAnalytics: SendAnalyticsEventHandler;
    setInsightVisibility: (insightId: string, isVisible: boolean) => void;
};

type RenderState = {
    customerId: string;
    insights: (InsightListItem & { key: string })[];
    renderId: string;
    selectedInsightTypes: InsightType[];
    visibleInsights: string[];
};

const allInsightTypes: InsightType[] = [
    InsightType.CHURN_WARNING,
    InsightType.GLOBAL_CHURN_WARNING,
    InsightType.CROSS_SELL,
    InsightType.FIRST_PURCHASE,
    InsightType.SHARE_OF_WALLET,
    InsightType.PREVIOUSLY_PURCHASED,
    InsightType.INDUSTRY_TOP,
    InsightType.GLOBAL_TOP,
];

export function useInsightsWithAnalytics({ customerId }: UseInsightsAnalyticsOptions): InsightsAnalyticsHook {
    const sendEvent = useAnalytics();
    const [sortBy, setSortByState] = useState<InsightSortBy>('recommended');

    const stateRef = useRef<RenderState | undefined>(undefined);

    stateRef.current ??= {
        customerId,
        insights: [],
        renderId: '',
        selectedInsightTypes: allInsightTypes,
        visibleInsights: [],
    };

    const { data, isLoading } = api.platoUser.query.insights.getManyInsights.getListWithFilters.useQuery({
        customerId,
        insightTypes: allInsightTypes,
    });

    const renderState = useMemo(() => {
        if (isLoading) {
            return stateRef.current;
        }
        const renderId = uuid();

        // Sort insights based on sortBy state
        const sortedInsights =
            data?.toSorted((a, b) => {
                if (sortBy === 'value') {
                    // Sort by absolute_number descending for value sort
                    return (b.insight.absolute_number ?? 0) - (a.insight.absolute_number ?? 0);
                } else {
                    // Sort by rank ascending for recommended sort (default)
                    return (a.insight.rank ?? 0) - (b.insight.rank ?? 0);
                }
            }) ?? [];

        // we specify a key that changes with the render, so that the component
        // is remounted and triggers view events again
        stateRef.current = {
            customerId,
            insights: sortedInsights.map((x) => ({ ...x, key: renderId + x.insight.id })),
            renderId: uuid(),
            selectedInsightTypes: allInsightTypes,
            visibleInsights: [],
        };
        return stateRef.current;
    }, [customerId, data, isLoading, sortBy]);

    const sendAnalytics = useCallback(
        (event: SendAnalyticsEventData) => {
            if (!stateRef.current) {
                return;
            }
            const index = stateRef.current.insights.findIndex(
                (x) => x.insight.id === event.payload.sales_insight_id,
            );
            if (index === -1) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we checked the index
            const { insight } = stateRef.current.insights[index]!;

            const baseData: SalesInsightEventPayload = {
                customer_id: insight.customer_id,
                insight_ids_visible: stateRef.current.visibleInsights,
                position_within_filter: index + 1,
                rank: insight.rank ?? 0,
                render_id: stateRef.current.renderId,
                sales_insight_id: insight.id,
                sales_insight_type: insight.insight_type,
            };
            if (
                event.event === 'sales_insight_completed' ||
                event.event === 'sales_insight_not_relevant' ||
                event.event === 'sales_insight_expanded_entity_clicked'
            ) {
                sendEvent(event.event, { ...baseData, ...event.payload });
            } else {
                sendEvent(event.event, baseData);
            }
        },
        [sendEvent],
    );

    const setInsightVisibility = useCallback((insightId: string, isVisible: boolean) => {
        const currentState = stateRef.current;
        if (!currentState) {
            return;
        }
        if (isVisible) {
            currentState.visibleInsights.push(insightId);
            // make sure array is sorted by display order
            currentState.visibleInsights = currentState.insights
                .filter(({ insight }) => currentState.visibleInsights.includes(insight.id))
                .map(({ insight }) => insight.id);
        } else {
            currentState.visibleInsights = currentState.visibleInsights.filter((x) => x !== insightId);
        }
    }, []);

    useEffect(() => {
        if (!renderState?.renderId) {
            return;
        }
        sendEvent('sales_insight_list_rendered', {
            customer_id: renderState.customerId,
            insight_ids: renderState.insights.map(({ insight }) => insight.id),
            insight_types_filter: renderState.selectedInsightTypes,
            render_id: renderState.renderId,
        });
    }, [renderState, sendEvent]);

    const setSortBy = useCallback(
        (newSortBy: InsightSortBy) => {
            if (newSortBy !== sortBy) {
                sendEvent('sales_insight_sort_dropdown_clicked', {
                    from_value: sortBy,
                    to_value: newSortBy,
                });
                setSortByState(newSortBy);
            }
        },
        [sortBy, sendEvent],
    );

    return {
        sendAnalytics,
        setInsightVisibility,

        insights: renderState?.insights ?? [],
        isLoading,
        sortBy,
        setSortBy,
    };
}
